import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Button,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faPen, faTrash, faSpinner, faPlus } from '@fortawesome/free-solid-svg-icons';
import { guardWrite, canWrite } from '../utils/permissions.js';
import ConfirmationModal from './confirmationModal.js';
import './CredentialsManager.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;

// The credential_type values the backend accepts for the M1 auth methods.
// (oauth_client_secret is deferred — see skill-flow-builder/12-custom-api-credentials.md.)
const TYPES = ['bearer', 'api_key', 'basic_password'];
const NAME_RE = /^[A-Za-z0-9_-]{1,100}$/;

function authHeaders(json = false) {
  const h = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

// Workspace-level vault UI: the org's stored API credentials a skill's http tool
// references by name (auth.credential_ref). Secrets are write-only — created/rotated
// here, encrypted server-side, and NEVER returned (rows show only a last-4 hint).
export default function CredentialsManager() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | {} (new) | {id,...} (edit)
  const [form, setForm] = useState({ name: '', credential_type: 'bearer', secret: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null); // the credential the delete modal asks about
  const writable = canWrite();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}credentials`, { headers: authHeaders() });
      const data = res.ok ? await res.json() : {};
      setItems(Array.isArray(data.credentials) ? data.credentials : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    if (!guardWrite()) return;
    setForm({ name: '', credential_type: 'bearer', secret: '' });
    setError('');
    setEditing({});
  };

  const openEdit = (c) => {
    if (!guardWrite()) return;
    setForm({ name: c.name, credential_type: c.credential_type, secret: '' });
    setError('');
    setEditing(c);
  };

  const close = () => { setEditing(null); setError(''); };

  const isEdit = !!(editing && editing.id);

  const save = async () => {
    if (!guardWrite()) return;
    const name = (form.name || '').trim();
    if (!isEdit && !NAME_RE.test(name)) { setError(t('cred_error_name')); return; }
    if (!isEdit && !(form.secret || '').trim()) { setError(t('cred_error_secret')); return; }
    setSaving(true);
    setError('');
    try {
      let res;
      if (isEdit) {
        const body = { credential_type: form.credential_type };
        if ((form.secret || '').trim()) body.secret = form.secret;
        res = await fetch(`${API_URL}credentials/${editing.id}`, {
          method: 'PUT', headers: authHeaders(true), body: JSON.stringify(body),
        });
      } else {
        res = await fetch(`${API_URL}credentials`, {
          method: 'POST', headers: authHeaders(true),
          body: JSON.stringify({ name, credential_type: form.credential_type, secret: form.secret }),
        });
      }
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail; } catch { /* ignore */ }
        setError(detail || t('cred_error_generic'));
        setSaving(false);
        return;
      }
      setSaving(false);
      close();
      load();
    } catch {
      setError(t('cred_error_generic'));
      setSaving(false);
    }
  };

  const askRemove = (c) => {
    if (!guardWrite()) return;
    setDeleting(c);
  };

  const remove = async (c) => {
    try {
      const res = await fetch(`${API_URL}credentials/${c.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (res.ok) load();
    } catch { /* the global fetch interceptor surfaces errors */ }
  };

  const usedLabel = (c) => {
    if (!c.last_used_at) return t('cred_used_never');
    try {
      const when = new Date(c.last_used_at).toLocaleDateString(i18n.language || 'en');
      return t('cred_used_at', { when });
    } catch {
      return t('cred_used_never');
    }
  };

  return (
    <div className="cred-mgr">
      <div className="cred-mgr-head">
        <div>
          <h2 className="cred-mgr-title">{t('cred_title')}</h2>
          <p className="cred-mgr-sub">{t('cred_subtitle')}</p>
        </div>
        {writable && (
          <button type="button" className="cred-add-btn" onClick={openNew}>
            <FontAwesomeIcon icon={faPlus} /> {t('cred_add')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="cred-loading"><FontAwesomeIcon icon={faSpinner} spin /> {t('cred_loading')}</div>
      ) : items.length === 0 ? (
        <p className="cred-empty">{t('cred_none')}</p>
      ) : (
        <ul className="cred-list">
          {items.map((c) => (
            <li key={c.id} className="cred-row">
              <span className="cred-row-icon"><FontAwesomeIcon icon={faKey} /></span>
              <span className="cred-row-main">
                <span className="cred-row-name">{c.name}</span>
                <span className="cred-row-meta">
                  <span className="cred-type-chip">{t(`cred_type_${c.credential_type}`)}</span>
                  {c.hint_last4 && <span className="cred-hint">{t('cred_hint_ends', { last4: c.hint_last4 })}</span>}
                  <span className="cred-used">{usedLabel(c)}</span>
                </span>
              </span>
              {writable && (
                <span className="cred-row-actions">
                  <button type="button" className="cred-icon-btn" onClick={() => openEdit(c)} aria-label={t('cred_edit')}>
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                  <button type="button" className="cred-icon-btn cred-danger" onClick={() => askRemove(c)} aria-label={t('cred_delete')}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!editing} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>{isEdit ? t('cred_edit_title') : t('cred_add_title')}</DialogTitle>
        <DialogContent>
          <div className="cred-form">
            <TextField
              label={t('cred_name_label')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              helperText={t('cred_name_help')}
              size="small"
              fullWidth
              disabled={isEdit}
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="cred-type-label">{t('cred_type_label')}</InputLabel>
              <Select
                labelId="cred-type-label"
                label={t('cred_type_label')}
                value={form.credential_type}
                onChange={(e) => setForm({ ...form, credential_type: e.target.value })}
              >
                {TYPES.map((ty) => (
                  <MenuItem key={ty} value={ty}>{t(`cred_type_${ty}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('cred_secret_label')}
              type="password"
              value={form.secret}
              onChange={(e) => setForm({ ...form, secret: e.target.value })}
              helperText={isEdit ? t('cred_secret_keep_help') : t('cred_secret_help')}
              size="small"
              fullWidth
              autoComplete="new-password"
            />
            {error && <p className="cred-error">{error}</p>}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>{t('cred_cancel')}</Button>
          <Button onClick={save} variant="contained" disabled={saving}
                  sx={{ backgroundColor: '#5616ea', '&:hover': { backgroundColor: '#4a12c9' } }}>
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : t('cred_save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove(deleting)}
        itemName={deleting?.name}
        note={t('cred_delete_note')}
      />
    </div>
  );
}
